import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider'
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService';
import { getErrorMessage } from '../../../shared/errorMessage';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { reportUserVisibleFailure } from '../../../shared/sentry/sentryUser';
import { isAuthErrorMessage } from '../../../shared/supabase/invokeErrorUtils';
import { logout } from '../../application/authSession';
import { ensureMyOrganization, createAdditionalOrganization } from '../../../onboarding/application/onboardingService';
import { fetchMyOrganizations } from '../../../shell/session/organizationSwitcherService';
import { rebootstrapAfterAdditionalOrganization } from '../../../shell/session/organizationSwitchFlow';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import {
  filterBusinessTypesForOnboardingPicker,
  getProductionOnboardingBusinessTypePickerSeed,
} from '../../../onboarding/application/onboardingBusinessTypeVisibility';
import {
  expandBusinessTypesToPickerItems,
  fetchOnboardingPicker,
  type OnboardingPickerItem,
} from '../../../onboarding/application/onboardingPickerService';
import {
  buildOperationModePatchFromOtherFeatures,
  buildOnboardingCapabilityDisables,
  resolveTemplateFromOtherFeatures,
  toggleOtherFeature,
  type OtherFeatureGroup,
  type OtherFeatureOptionId,
  DEFAULT_OTHER_FEATURE_GROUPS,
} from '../../../onboarding/application/otherFeatureCatalog';
import type { BusinessTypeCode } from '../../../shell/session/domain/businessProfile';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import {
  getOrganizationSetupSteps,
  type OnboardingWizardStepId,
} from './onboardingWizardConfig';
import {
  isOtherRubroPickerCode,
  OTHER_RUBRO_PICKER_CODE,
  resolveBusinessTypeFromOtherTemplate,
  resolveBusinessTypeFromLabelAndTemplate,
} from '../../../onboarding/application/otherRubroFlow';
import { createOtherRubroSupportTicketIfNeeded } from '../../../onboarding/application/otherRubroSupportTicket';
import {
  applyOnboardingCapabilityDisables,
  applyOnboardingFeatureOverrides,
} from '../../../onboarding/application/onboardingFeatureOverrides';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { trackOnboardingFunnel } from '../../../onboarding/application/onboardingFunnel';
import { applyGastronomyStockMode } from '../../../onboarding/application/applyGastronomyStockMode';
import type { GastronomyStockMode } from '../../../onboarding/application/gastronomyStockMode';
import { syncCatalogSnapshotFromRemote } from '../../../catalog/infra-api';

export type OnboardingStateMachine = {
  steps: ReturnType<typeof getOrganizationSetupSteps>;
  stepIndex: number;
  currentStep: ReturnType<typeof getOrganizationSetupSteps>[number];
  isLastStep: boolean;
  canProceed: boolean;
  businessName: string;
  setBusinessName: (v: string) => void;
  /** Same as businessName — proto: un solo campo. */
  branchName: string;
  /** Nombre visible del dueño (customer-facing en turnos). Opcional. */
  ownerName: string;
  setOwnerName: (v: string) => void;
  pickerItems: OnboardingPickerItem[];
  selectedPickerKey: string;
  selectedBusinessType: BusinessTypeCode;
  /** QSF or weight_retail identity flavor (identity only; same ERP caps). */
  selectedVerticalFlavor: string | null;
  selectPickerItem: (item: OnboardingPickerItem) => void;
  enteredViaOtherRubro: boolean;
  otherRubroLabel: string;
  setOtherRubroLabel: (v: string) => void;
  otherFeatureGroups: OtherFeatureGroup[];
  otherFeatureIds: OtherFeatureOptionId[];
  toggleOtherFeatureId: (id: OtherFeatureOptionId) => void;
  resolvedBusinessTypeCode: BusinessTypeCode;
  loadingTypes: boolean;
  typesLoadError: string | undefined;
  /** Reintenta la carga del picker tras un error de red. */
  retryPickerTypes: () => void;
  submitting: boolean;
  error: string | undefined;
  logoutConfirmVisible: boolean;
  setLogoutConfirmVisible: (v: boolean) => void;
  multiBusinessEnabled: boolean;
  /** Ticket de soporte por rubro Otros: hint en pantalla Listo. */
  otherRubroSupportNote: boolean;
  gastronomyStockMode: GastronomyStockMode;
  setGastronomyStockMode: (mode: GastronomyStockMode) => void;
  handleBack: (canGoBack: boolean, goBack: () => void) => void;
  handleConfirmLogout: () => void;
  handleNext: (
    onComplete?: () => void | Promise<void>,
    goBack?: () => void,
    navigation?: NavigationProp<ParamListBase>,
  ) => Promise<OnboardingSubmitOutcome | void>;
};

export type OrganizationSetupMode = 'initial' | 'additional';

/**
 * Resultado de `handleNext` en el último paso: indica si la organización fue
 * creada (o reutilizada de forma idempotente). El wizard usa este valor para
 * transicionar a la fase `success` de forma explícita en el handler de submit,
 * sin depender de un `useEffect` sobre `submitting`.
 */
export type OnboardingSubmitOutcome = { created: boolean };

export function useOnboardingStateMachine(options?: {
  setupMode?: OrganizationSetupMode;
}): OnboardingStateMachine {
  const setupMode = options?.setupMode ?? 'initial';
  const { flags } = useGlobalFeatureFlags();
  const multiBusinessEnabled = isGlobalFeatureOn(flags, 'multi_business_profiles_enabled');
  const pickerSeed = useMemo(() => {
    const seed = multiBusinessEnabled ? getProductionOnboardingBusinessTypePickerSeed() : [];
    return expandBusinessTypesToPickerItems(filterBusinessTypesForOnboardingPicker(seed));
  }, [multiBusinessEnabled]);

  const [stepId, setStepId] = useState<OnboardingWizardStepId>(() =>
    multiBusinessEnabled ? 'business-type' : 'identity',
  );
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState(() => {
    const meta = useAuthStore.getState().session?.user?.user_metadata as
      | Record<string, unknown>
      | undefined;
    const prefill = meta?.full_name ?? meta?.name;
    return typeof prefill === 'string' ? prefill : '';
  });
  const [pickerItems, setPickerItems] = useState<OnboardingPickerItem[]>(pickerSeed);
  const [selectedPickerKey, setSelectedPickerKey] = useState(
    () => pickerSeed[0]?.picker_key ?? 'retail_counter',
  );
  const [otherRubroLabel, setOtherRubroLabel] = useState('');
  const [otherFeatureIds, setOtherFeatureIds] = useState<OtherFeatureOptionId[]>([]);
  const [otherFeatureGroups, setOtherFeatureGroups] = useState<OtherFeatureGroup[]>([
    ...DEFAULT_OTHER_FEATURE_GROUPS,
  ]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [typesLoadError, setTypesLoadError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [logoutConfirmVisible, setLogoutConfirmVisible] = useState(false);
  /** Ticket Otros creado: se muestra en success, no como toast encima del ✓. */
  const [otherRubroSupportNote, setOtherRubroSupportNote] = useState(false);
  const [gastronomyStockMode, setGastronomyStockMode] = useState<GastronomyStockMode>('B');

  const selectedItem = useMemo(
    () => pickerItems.find((i) => i.picker_key === selectedPickerKey) ?? pickerItems[0],
    [pickerItems, selectedPickerKey],
  );
  const selectedBusinessType = (selectedItem?.code ?? 'retail_counter') as BusinessTypeCode;
  const selectedVerticalFlavor =
    typeof selectedItem?.vertical_flavor === 'string' && selectedItem.vertical_flavor.trim()
      ? selectedItem.vertical_flavor.trim()
      : null;

  const enteredViaOtherRubro = isOtherRubroPickerCode(selectedBusinessType);
  const includeOtherRubroStep = multiBusinessEnabled && enteredViaOtherRubro;

  const otherRubroTemplate = useMemo(
    () => (enteredViaOtherRubro ? resolveTemplateFromOtherFeatures(otherFeatureIds) : null),
    [enteredViaOtherRubro, otherFeatureIds],
  );

  const resolvedBusinessTypeCode = useMemo(() => {
    if (!enteredViaOtherRubro || !otherRubroTemplate) return selectedBusinessType;
    return resolveBusinessTypeFromOtherTemplate(otherRubroTemplate);
  }, [enteredViaOtherRubro, otherRubroTemplate, selectedBusinessType]);

  const includeGastronomyStockStep =
    multiBusinessEnabled &&
    !enteredViaOtherRubro &&
    resolvedBusinessTypeCode === 'quick_service_food';

  const steps = useMemo(
    () =>
      getOrganizationSetupSteps(multiBusinessEnabled, {
        includeOtherRubroStep,
        includeGastronomyStockStep,
      }),
    [multiBusinessEnabled, includeOtherRubroStep, includeGastronomyStockStep],
  );

  useEffect(() => {
    if (steps.some((s) => s.id === stepId)) return;
    setStepId(steps[0]?.id ?? 'identity');
  }, [steps, stepId]);

  const stepIndex = Math.max(0, steps.findIndex((s) => s.id === stepId));
  const currentStep = steps[stepIndex] ?? steps[0];
  const isLastStep = stepIndex >= steps.length - 1;

  const loadPickerTypes = useCallback(() => {
    let cancelled = false;
    setLoadingTypes(true);
    setTypesLoadError(undefined);
    void fetchOnboardingPicker('AR')
      .then((payload) => {
        if (cancelled) return;
        const visible = payload.items.filter((item) => {
          if (item.code === OTHER_RUBRO_PICKER_CODE) return true;
          return filterBusinessTypesForOnboardingPicker([item]).length > 0;
        });
        const items =
          visible.length > 0
            ? visible
            : expandBusinessTypesToPickerItems(
                filterBusinessTypesForOnboardingPicker(
                  getProductionOnboardingBusinessTypePickerSeed(),
                ),
              );
        setPickerItems(items);
        setOtherFeatureGroups(payload.other_feature_groups);
        if (items[0]?.picker_key) setSelectedPickerKey(items[0].picker_key);
        if (items.length === 0) {
          setTypesLoadError('No hay rubros disponibles. Revisá la conexión e intentá de nuevo.');
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = 'No se pudieron cargar los rubros.';
        console.warn('[Onboarding] list_onboarding_picker', e);
        const fallback = expandBusinessTypesToPickerItems(
          filterBusinessTypesForOnboardingPicker(getProductionOnboardingBusinessTypePickerSeed()),
        );
        setPickerItems(fallback);
        if (fallback[0]?.picker_key) setSelectedPickerKey(fallback[0].picker_key);
        setTypesLoadError(
          fallback.length > 0
            ? 'No pudimos actualizar la lista desde el servidor. Mostramos Kiosco y Almacén.'
            : msg,
        );
        reportUserVisibleFailure({ module: 'auth', op: 'onboarding.load_business_types', error: e });
      })
      .finally(() => {
        if (!cancelled) setLoadingTypes(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!multiBusinessEnabled) return;
    return loadPickerTypes();
  }, [multiBusinessEnabled, loadPickerTypes]);

  useEffect(() => {
    if (!isOtherRubroPickerCode(selectedBusinessType)) {
      setOtherFeatureIds([]);
      setOtherRubroLabel('');
    }
  }, [selectedBusinessType]);

  const selectPickerItem = (item: OnboardingPickerItem) => {
    setSelectedPickerKey(item.picker_key);
  };

  const toggleOtherFeatureId = (id: OtherFeatureOptionId) => {
    setOtherFeatureIds((prev) => toggleOtherFeature(prev, id));
  };

  const canProceed = useMemo(() => {
    const id = currentStep?.id;
    if (!id) return false;
    if (id === 'business-type') return Boolean(selectedPickerKey) && !loadingTypes;
    if (id === 'gastronomy-stock-mode') {
      return gastronomyStockMode === 'A' || gastronomyStockMode === 'B' || gastronomyStockMode === 'C';
    }
    if (id === 'other-rubro') {
      return otherFeatureIds.length >= 1 && otherRubroLabel.trim().length >= 2;
    }
    if (id === 'identity') {
      return businessName.trim().length >= 2 && ownerName.trim().length >= 2;
    }
    return true;
  }, [
    businessName,
    ownerName,
    currentStep?.id,
    loadingTypes,
    otherFeatureIds.length,
    otherRubroLabel,
    selectedPickerKey,
    gastronomyStockMode,
  ]);

  const handleBack = (canGoBack: boolean, goBack: () => void) => {
    setError(undefined);
    if (stepIndex > 0) {
      const prev = steps[stepIndex - 1];
      if (prev) setStepId(prev.id);
      return;
    }
    if (canGoBack) {
      goBack();
      return;
    }
    setLogoutConfirmVisible(true);
  };

  const handleConfirmLogout = () => {
    setLogoutConfirmVisible(false);
    void logout('onboarding_leave');
  };

  const handleNext = async (
    onComplete?: () => void | Promise<void>,
    goBack?: () => void,
    navigation?: NavigationProp<ParamListBase>,
  ): Promise<OnboardingSubmitOutcome | void> => {
    if (!canProceed) {
      if (currentStep?.id === 'identity') {
        setError(
          businessName.trim().length >= 2
            ? 'Escribí tu nombre.'
            : 'Escribí el nombre de tu local.',
        );
      }
      if (currentStep?.id === 'other-rubro') {
        setError('Contanos qué vendés y marcá al menos una opción.');
      }
      return;
    }
    setError(undefined);
    if (isLastStep) {
      const name = businessName.trim();
      if (!name) {
        setError('Escribí el nombre de tu local.');
        return;
      }
      setSubmitting(true);
      try {
        const requestedLabel = enteredViaOtherRubro ? otherRubroLabel.trim() : null;
        const createOrg =
          setupMode === 'additional' ? createAdditionalOrganization : ensureMyOrganization;
        const operationModePatch = enteredViaOtherRubro
          ? buildOperationModePatchFromOtherFeatures(otherFeatureIds)
          : {};
        // Resolver rubro via RPC si hay label libre, fallback a template si falla.
        const effectiveBusinessTypeCode = enteredViaOtherRubro && otherRubroTemplate && requestedLabel
          ? await resolveBusinessTypeFromLabelAndTemplate(requestedLabel, otherRubroTemplate)
          : multiBusinessEnabled ? resolvedBusinessTypeCode : 'retail_counter';
        const pickerPackCode =
          typeof selectedItem?.onboarding_pack_code === 'string'
            ? selectedItem.onboarding_pack_code
            : null;
        const result = await createOrg(name, name, {
          businessTypeCode: effectiveBusinessTypeCode,
          countryCode: 'AR',
          applyCatalogPack: false,
          verticalFlavor: selectedVerticalFlavor,
          onboardingPackCode: pickerPackCode,
          requestedVerticalLabel: requestedLabel,
          operationModePatch,
          ownerName: ownerName.trim() || null,
        });

        if (
          result.created &&
          includeGastronomyStockStep &&
          effectiveBusinessTypeCode === 'quick_service_food'
        ) {
          try {
            await applyGastronomyStockMode(gastronomyStockMode, { applyPack: true });
            try {
              await syncCatalogSnapshotFromRemote({ force: true, forceFull: true });
            } catch (syncErr) {
              console.warn('[Onboarding] sync catálogo tras modo stock', syncErr);
            }
          } catch (modeErr) {
            console.warn('[Onboarding] apply_gastronomy_stock_mode', modeErr);
          }
        }
        // Funnel (#1362): org creada OK (initial y additional). Acá, no en el
        // wizard: solo este punto sabe que createOrg no tiró error.
        trackOnboardingFunnel('onboarding_completed', {
          setup_mode: setupMode,
          business_type: effectiveBusinessTypeCode,
          created: result.created,
        });

        if (enteredViaOtherRubro && otherRubroTemplate && requestedLabel && result.created) {
          const userId = useAuthStore.getState().session?.user?.id;
          const branchId = result.branch_ids[0] ?? null;
          if (userId) {
            try {
              await createOtherRubroSupportTicketIfNeeded({
                organizationId: result.organization_id,
                branchId,
                userId,
                source:
                  setupMode === 'additional' ? 'additional_org_other_rubro' : 'onboarding_other_rubro',
                requestedVerticalLabel: requestedLabel,
                operationTemplate: otherRubroTemplate,
                resolvedBusinessTypeCode: effectiveBusinessTypeCode,
                pickerBusinessTypeCode: OTHER_RUBRO_PICKER_CODE,
                selectedFeatureIds: otherFeatureIds,
              });
              if (setupMode === 'initial') {
                setOtherRubroSupportNote(true);
              } else {
                showInfo('Te escribimos por soporte si hace falta.', undefined, 4000);
              }
            } catch (ticketErr) {
              console.warn('[Onboarding] other rubro ticket failed', ticketErr);
            }
          }

          if (enteredViaOtherRubro) {
            try {
              await applyOnboardingCapabilityDisables(
                buildOnboardingCapabilityDisables(otherFeatureIds),
              );
            } catch (disableErr) {
              console.warn('[Onboarding] capability disable failed', disableErr);
            }
          }

          // #900 Phase 2: apply feature overrides for caps the rubro doesn't have
          if (otherFeatureIds.length > 0) {
            try {
              const overrideResult = await applyOnboardingFeatureOverrides(
                result.organization_id,
                otherFeatureIds,
              );
              if (overrideResult.applied.length > 0) {
                showSuccess(
                  'Funciones extra activadas',
                  `Sumamos ${overrideResult.applied.length} ${overrideResult.applied.length === 1 ? 'función que pediste' : 'funciones que pediste'} que no están en tu rubro. Las podés ver en Funciones del negocio.`,
                  6000,
                );
              }
            } catch (overrideErr) {
              console.warn('[Onboarding] feature override failed', overrideErr);
            }
          }
        }

        if (setupMode === 'additional' && navigation) {
          const list = await fetchMyOrganizations({ force: true });
          await rebootstrapAfterAdditionalOrganization(navigation, list);
          await onComplete?.();
          return;
        }

        // Initial: no navegar acá. El wizard muestra «Listo» (fase success) y
        // recién ahí el CTA llama onComplete. Si llamamos onComplete/goBack ahora,
        // el gate (hasOrganization) desmonta el wizard y salta el paso 3.
        // Devolvemos el outcome ({ created }) para que el wizard transicione a
        // `success` de forma explícita en el handler de submit (sin useEffect).
        if (setupMode === 'initial') {
          return { created: result.created };
        }

        await onComplete?.();
        goBack?.();
      } catch (e: unknown) {
        const rawMessage = getErrorMessage(e) ?? '';
        if (isAuthErrorMessage(rawMessage)) {
          // Sesión vencida mid-onboarding: toast + copy claros; no Sentry (VUELTO-NEGOCIO-2B).
          setError('Tu sesión expiró. Cerrá sesión e iniciá de nuevo.');
          showError('Sesión vencida', 'Cerrá sesión e iniciá de nuevo para terminar de abrir tu local.');
          return;
        }
        setError('No se pudo completar la acción. Probá de nuevo.');
        presentUnexpectedFailure({
          module: 'auth',
          op: 'onboarding.complete',
          error: e,
          title: 'No se pudo completar la acción.',
          userMessage: rawMessage || 'Probá de nuevo en un momento.',
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }
    const next = steps[Math.min(steps.length - 1, stepIndex + 1)];
    if (next) setStepId(next.id);
  };

  return {
    steps,
    stepIndex,
    currentStep,
    isLastStep,
    canProceed,
    businessName,
    setBusinessName,
    branchName: businessName,
    ownerName,
    setOwnerName,
    pickerItems,
    selectedPickerKey,
    selectedBusinessType,
    selectedVerticalFlavor,
    selectPickerItem,
    enteredViaOtherRubro,
    otherRubroLabel,
    setOtherRubroLabel,
    otherFeatureGroups,
    otherFeatureIds,
    toggleOtherFeatureId,
    resolvedBusinessTypeCode,
    loadingTypes,
    typesLoadError,
    retryPickerTypes: loadPickerTypes,
    submitting,
    error,
    logoutConfirmVisible,
    setLogoutConfirmVisible,
    otherRubroSupportNote,
    gastronomyStockMode,
    setGastronomyStockMode,
    multiBusinessEnabled,
    handleBack,
    handleConfirmLogout,
    handleNext,
  };
}
